import type { Text } from '@kkf/design'
import { appUrl } from '@kkf/design'
import { LINKS } from './links'

/**
 * The Foundation's work — the seven areas named in `docs/resources/Case for Support`
 * under "Our Response", in the order that document lists them.
 *
 * Five of the seven point at one of the venture apps, by absolute URL — they are separate
 * deployments now. The rest point at the placeholder service-details page, which has not
 * been written yet.
 */

const DETAILS_ROUTE = '/services/clean-water-supply'

/**
 * One area of the Foundation's work, as DATA rather than as a card's props.
 *
 * `ServiceCardProps` takes plain strings — the design system renders what it is handed —
 * so the page maps this into that. It was typed as the props directly until 2026-09-18,
 * which worked only while the two shapes agreed.
 */
export interface ServiceEntry {
  image: string
  imageAlt?: Text
  title: Text
  descr: Text
  to?: string
  href?: string
}

export const SERVICES: ServiceEntry[] = [
  {
    image: '/assets/img/garage-repair-bench.jpg',
    imageAlt: { en: 'A mechanic bent over a stripped motorcycle, working on it by hand', mn: 'Задлагдсан мотоцикл дээр бөхийж, гараараа ажиллаж буй механикч' },
    title: { en: 'Youth Mentoring & Skills', mn: 'Залуучуудын зөвлөгөө ба ур чадвар' },
    descr:
      { en: 'Training in automotive work, trades, robotics, technology, creative arts and life skills, alongside mentorship and spiritual guidance — helping young people grow closer to God and recognise the dignity of all work.', mn: 'Авто засвар, мэргэжил, робот техник, технологи, урлаг, амьдралын ур чадварын сургалт — зөвлөгөө, сүнслэг чиглүүлгийн хамт. Энэ нь залуучуудыг Бурханд ойртож, аливаа хөдөлмөрийн нэр төрийг таньж мэдэхэд тусална.' },
    href: LINKS.garage,
  },
  {
    image: '/assets/img/resort-cabins-autumn.jpg',
    title: { en: 'Christ-Centered Resort', mn: 'Христэд төвлөрсөн амралтын газар' },
    descr:
      { en: 'A place for rest, recreation, spiritual growth, retreats, camps and community events, with customised Mongolia tour packages for foreign guests.', mn: 'Амрах, зугаацах, сүнслэг өсөх, цугларалт, зуслан, олон нийтийн арга хэмжээний газар — гадаадын зочдод зориулсан тохируулсан Монголын аяллын багцтай.' },
    href: LINKS.resort,
  },
  {
    image: '/assets/img/mongolia-village-overlook.jpg',
    imageAlt: { en: 'A woman in a Mongolia-embroidered jacket looking out over a steppe settlement', mn: 'Монгол хээтэй хүрэмтэй эмэгтэй талын суурин руу харж байна' },
    title: { en: 'Missionary Tourism & Short-Term Missions', mn: 'Номлолын аялал жуулчлал ба богино хугацааны номлол' },
    descr:
      { en: 'Fully customisable tours that let visitors serve alongside local churches and organisations while experiencing Mongolia’s landscapes, natural beauty and culture.', mn: 'Зочдод Монголын байгаль, гоо үзэсгэлэн, соёлыг мэдрэхийн зэрэгцээ орон нутгийн чуулган, байгууллагуудтай хамт үйлчлэх боломж олгодог бүрэн тохируулж болох аялалууд.' },
    href: LINKS.resort,
  },
  {
    image: '/assets/img/about-family-steppe.jpg',
    title: { en: 'Marriage & Family', mn: 'Гэрлэлт ба гэр бүл' },
    descr:
      { en: 'Free or supported weddings, premarital preparation and family mentoring that encourage healthy marriages and help couples build a strong foundation for family life.', mn: 'Үнэгүй буюу дэмжлэгтэй хурим, гэрлэлтийн өмнөх бэлтгэл, гэр бүлийн зөвлөгөө — эрүүл гэр бүлийг дэмжиж, хосуудад гэр бүлийн амьдралын бат бөх суурийг тавихад тусална.' },
    to: DETAILS_ROUTE,
  },
  {
    image: '/assets/img/garage-workshop-lift.jpg',
    title: { en: 'Christian Business Platform', mn: 'Христийн бизнесийн платформ' },
    descr:
      { en: 'King of Kings Partners — a nationwide website and app connecting customers with Christian businesses and services, and encouraging kingdom collaboration and partnerships.', mn: 'King of Kings Partners — үйлчлүүлэгчдийг Христийн бизнес, үйлчилгээтэй холбож, хаанчлалын хамтын ажиллагаа, түншлэлийг дэмждэг улс даяарх вэбсайт, аппликейшн.' },
    href: LINKS.partners,
  },
  {
    image: '/assets/img/about-ger-steppe.jpg',
    title: { en: 'Holistic Health Initiatives', mn: 'Бүрэн хүртээмжтэй эрүүл мэндийн санаачилга' },
    descr:
      { en: 'Promoting natural foods, medicinal herbs, healthy living and practical wellness education — health taught as part of a whole life.', mn: 'Байгалийн хүнс, эмийн ургамал, эрүүл амьдрал, практик эрүүл мэндийн боловсролыг дэмжих — эрүүл мэндийг бүхэл амьдралын нэг хэсэг болгон заадаг.' },
    to: DETAILS_ROUTE,
  },
  {
    image: '/assets/img/resort-reading-room.jpg',
    title: { en: 'Community & Wellness', mn: 'Хамт олон ба сайн сайхан байдал' },
    descr:
      { en: 'Spaces and programmes for churches, organisations, expatriates and communities, including an English-language library for foreign visitors.', mn: 'Чуулган, байгууллага, гадаадын иргэд, олон нийтэд зориулсан орон зай, хөтөлбөрүүд — үүнд гадаадын зочдод зориулсан англи хэлний номын сан багтана.' },
    href: appUrl(LINKS, 'resort', '/library'),
  },
]

export const WHY_JOIN = {
  image: '/assets/img/why-join.jpg',
  subTitle: { en: 'Join us', mn: 'Бидэнтэй нэгдээрэй' },
  title: { en: 'Why We Need You Become a Volunteer', mn: 'Бид яагаад таныг сайн дурын ажилтан болохыг хүсэж байна вэ' },
  descr:
    { en: 'Lasting change happens when practical support is combined with sustainable opportunities to grow — and that takes people willing to give their time.', mn: 'Тогтвортой өөрчлөлт нь практик дэмжлэгийг өсөх тогтвортой боломжтой хослуулсан үед бий болдог — үүнд цагаа зарцуулахад бэлэн хүмүүс хэрэгтэй.' },
}
